import { fg } from '@atlaskit/platform-feature-flags/fg';
import { cachedRecommendedDocumentsQuery } from '@atlassian/search-client/recommended-documents';
import {
	type RecommendedDocument,
	type RecommendedDocumentEntity,
} from '@atlassian/search-client/recommended-documents/types';

/**
 * Default max number from backend is 20, we have maximum 3 slots for recommendations in pre-query,
 * but we're trying to fetch as many as possible so user would still be able to see more recommendations in post-query search results
 */
export const MAX_RECOMMENDED_DOCUMENTS = 20;

/** Entity types the query only asks for when `quick_find_recommendations_for_issue_pr` is on. */
const JIRA_RECOMMENDATION_TYPENAMES = ['JiraIssue', 'ExternalPullRequest'];

/**
 * Drops Jira work item and pull request recommendations while the gate is off.
 *
 * The query already omits their fragments, so this normally filters nothing. It
 * matters when one arrives anyway — most plausibly from a response cached before
 * the gate was turned off, since the response cache keys on the query variables and
 * not on the composed document. Without this, such a recommendation would occupy one
 * of the three pre-query slots and then render nothing, leaving a short section.
 */
const isSupportedRecommendation = (result: RecommendedDocument): boolean => {
	const typename = result.object?.__typename;

	if (typename && JIRA_RECOMMENDATION_TYPENAMES.includes(typename)) {
		return fg('quick_find_recommendations_for_issue_pr');
	}

	return true;
};

export const fetchRecommendedDocuments: (
	cloudId: string,
	userId: string,
	aggAbsoluteUrl: string | undefined,
	headers?: Record<string, string>,
	featureServiceModel?: string,
) => Promise<
	{
		id: string;
		title: string;
		data: RecommendedDocument;
		type: 'recommended-document';
	}[]
> = async (
	cloudId: string,
	userId: string,
	aggAbsoluteUrl: string | undefined,
	headers?: Record<string, string>,
	featureServiceModel?: string,
) => {
	const response = await cachedRecommendedDocumentsQuery({
		variables: {
			userId,
			tenantId: cloudId,
			caller: 'quick-find',
			maxNumberOfResults: MAX_RECOMMENDED_DOCUMENTS,
			// Sent for eligible users in the Quick Find 3P recommendation-model
			// experiment. When omitted, AGG falls back to its default model.
			...(featureServiceModel ? { featureServiceModel } : {}),
		},
		aggAbsoluteUrl,
		onlyRefreshIfExpired: true,
		returnExpired: false,
		headers,
	});

	const recommendations: RecommendedDocument[] =
		response.data?.contextService?.quickfindUserRecommendations || [];
	return recommendations
		.filter((r) => Boolean(r.object))
		.filter(isSupportedRecommendation)
		.map(mapEssentialData);
};

/**
 * The title is what pre-query client-side filtering (`filterSearchResults`)
 * matches the typed query against, so it must mirror what the row renders:
 * Jira issues render as `KEY: summary`.
 */
const getRecommendationTitle = (object?: RecommendedDocumentEntity): string => {
	switch (object?.__typename) {
		case 'ConfluencePage':
			return object.title || '';
		case 'ExternalDocument':
		case 'ExternalPullRequest':
			return object.displayName || '';
		case 'JiraIssue':
			return object.key && object.summary ? `${object.key}: ${object.summary}` : '';
		default:
			return '';
	}
};

export const mapEssentialData = <T extends RecommendedDocumentEntity>(
	result: RecommendedDocument<T>,
): {
	id: string;
	title: string;
	data: RecommendedDocument<T>;
	type: 'recommended-document';
} => {
	const object = result.object;
	const title = getRecommendationTitle(object);

	return {
		id: result.id,
		title,
		data: result,
		type: 'recommended-document' as const,
	};
};
